import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  FormGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DeviceInfoSection } from './DeviceInfoSection';
import {
  selectedDeviceDataAtom,
  selectedDeviceStateAtom,
  waterLevelFilteredAtom,
} from '../../../models/atoms/device_atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { MHMTypography } from '../MHM/MHMTypography';
import { DeviceGraph } from './DeviceGraph';
import { DeviceDataRangeStats } from './DeviceDataRangeStats';
import DownloadCSVModal from './DownloadCSVModal';
import { MHMLabelledCheckbox } from '../MHM/MHMLabelledCheckbox';
import { useDeviceDataPeriod } from '../../../hooks/useDeviceDataPeriod';
//import { useDeviceDataDateRange } from '../../../hooks/useDeviceDataDateRange';
import { device_controller } from '../../../controllers/device_controller';
import { DeviceScreenshotButton } from './DeviceScreenshotButton';
import { isFetchingDeviceDataAtom } from '../../../models/atoms/ui_atoms';
import {
  maxRainfallLevelSelector,
  maxWaterLevelSelector,
} from '../../../models/selectors/device_selectors';
import { DeviceDataPeriodRangeSelect } from './DeviceDataPeriodRangeSelect';

type Props = {
  session: string | null | undefined;
};

export function DeviceDataVisualisation({ session }: Props) {
  const { t } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const selectedDeviceData = useRecoilValue(selectedDeviceDataAtom);
  const setFiltered = useSetRecoilState(waterLevelFilteredAtom);
  const [showRawWaterLevel, setShowRawWaterLevel] = useState(false);
  const [showFilteredWaterLevel, setShowFilteredWaterLevel] = useState(true);
  const [showRainfall, setShowRainfall] = useState(false);
  const [showBenchHeight, setShowBenchHeight] = useState(false);
  const [showPipeDiameter, setShowPipeDiameter] = useState(false);
  const [showMaximums, setShowMaximums] = useState(true);
  const [showDownload, setShowDownload] = useState(false);
  const [fitYAxisToData, setFitYAxisToData] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const { periodType, periodStartDate, periodEndDate, changePeriodType } =
    useDeviceDataPeriod();

  const isFetchingData = useRecoilValue(isFetchingDeviceDataAtom);
  const maxWaterLevel = useRecoilValue(maxWaterLevelSelector);
  const maxRainfallLevel = useRecoilValue(maxRainfallLevelSelector);

  useEffect(() => {
    setFiltered(showFilteredWaterLevel);
  }, [setFiltered, showFilteredWaterLevel]);

  function toggleShowFilteredWaterLevel(event: ChangeEvent<HTMLInputElement>) {
    setShowFilteredWaterLevel(event.target.checked);
    setShowRawWaterLevel(!event.target.checked);
    setFiltered(event.target.checked);
    if (selectedDevice) {
      device_controller.get_device_data(
        selectedDevice.device_id,
        new Date((selectedDevice.first_data ?? 0) * 1000).setUTCHours(0, 0, 0) /
          1000,
        new Date((selectedDevice.last_data ?? 0) * 1000).setUTCHours(
          23,
          59,
          59,
        ) / 1000,
        session,
      );
    }
  }

  function toggleShowRawWaterLevel(event: ChangeEvent<HTMLInputElement>) {
    setShowRawWaterLevel(event.target.checked);
    setShowFilteredWaterLevel(!event.target.checked);
    setFiltered(!event.target.checked);
    if (selectedDevice) {
      device_controller.get_device_data(
        selectedDevice.device_id,
        new Date((selectedDevice.first_data ?? 0) * 1000).setUTCHours(0, 0, 0) /
          1000,
        new Date((selectedDevice.last_data ?? 0) * 1000).setUTCHours(
          23,
          59,
          59,
        ) / 1000,
        session,
      );
    }
  }

  function toggleShowRainfall(event: ChangeEvent<HTMLInputElement>) {
    setShowRainfall(event.target.checked);
  }

  function toggleShowBenchHeight(event: ChangeEvent<HTMLInputElement>) {
    setShowBenchHeight(event.target.checked);
  }

  function toggleShowPipeDiameter(event: ChangeEvent<HTMLInputElement>) {
    setShowPipeDiameter(event.target.checked);
  }

  useEffect(() => {
    changePeriodType(periodType);
  }, [changePeriodType, periodType, session]);

  if (!selectedDevice) {
    return null;
  }

  //console.log(periodStartDate);
  return (
    <DeviceInfoSection>
      <DeviceInfoSection.Heading>
        {t('Data Visualisation')}
      </DeviceInfoSection.Heading>
      <DeviceDataPeriodRangeSelect />
      <Grid container spacing={2}></Grid>
      <Grid container spacing={1} alignItems="flex-start">
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormGroup>
            <MHMLabelledCheckbox
              label={
                <MHMTypography color="primary">
                  {t('Water Level Filtered (mm)')}
                </MHMTypography>
              }
              checkboxProps={{
                checked: showFilteredWaterLevel,
                onChange: toggleShowFilteredWaterLevel,
              }}
            />
          </FormGroup>
          <FormGroup>
            <MHMLabelledCheckbox
              label={
                <MHMTypography color="primary">
                  {t('Water Level Raw (mm)')}
                </MHMTypography>
              }
              checkboxProps={{
                checked: showRawWaterLevel,
                onChange: toggleShowRawWaterLevel,
              }}
            />
          </FormGroup>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormGroup>
            <MHMLabelledCheckbox
              label={
                <MHMTypography color="rainfall">
                  {t('Rainfall Level (mm)')}
                </MHMTypography>
              }
              checkboxProps={{
                checked: showRainfall,
                onChange: toggleShowRainfall,
                iconColor: 'rainfall',
              }}
            />
          </FormGroup>
          <FormGroup>
            <MHMLabelledCheckbox
              label={
                <MHMTypography color="primary">
                  {t('Show Maximums')}
                </MHMTypography>
              }
              checkboxProps={{
                checked: showMaximums,
                onChange: e => setShowMaximums(e.target.checked),
              }}
            />
          </FormGroup>
        </Grid>
        {!!selectedDevice.bench_height_mm && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormGroup>
              <MHMLabelledCheckbox
                label={
                  <MHMTypography color="bench_height">
                    {t('Bench Height (mm)')}
                  </MHMTypography>
                }
                checkboxProps={{
                  checked: showBenchHeight,
                  onChange: toggleShowBenchHeight,
                  iconColor: 'bench_height',
                }}
              />
            </FormGroup>
          </Grid>
        )}
        {!!selectedDevice.pipe_diameter_mm && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormGroup>
              <MHMLabelledCheckbox
                label={
                  <MHMTypography color="pipe_diameter">
                    {t('Pipe Diameter (mm)')}
                  </MHMTypography>
                }
                checkboxProps={{
                  checked: showPipeDiameter,
                  onChange: toggleShowPipeDiameter,
                  iconColor: 'pipe_diameter',
                }}
              />
            </FormGroup>
          </Grid>
        )}
      </Grid>

      <Paper
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 0.15,
          mt: 0.5,
          mb: 0.5,
          borderRadius: 1,
          maxWidth: '100%',
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <Stack flexWrap="wrap" direction="row" alignItems="center" gap={1}>
          <MHMTypography color="primary" sx={{ lineHeight: 1 }}>
            {t('Water Level Max:')}
          </MHMTypography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={fitYAxisToData ? 'period' : 'device'}
            onChange={(_e, value) => {
              if (value === null) return;
              setFitYAxisToData(value === 'period');
            }}
            sx={{
              '& .MuiToggleButton-root': {
                px: 0.6,
                py: 0.2,
                minHeight: 0,
                fontSize: '1rem',
                lineHeight: 1.2,
                textTransform: 'none',
              },
            }}
          >
            <ToggleButton value="device" aria-label={t('Measureable Max')}>
              {t('Measureable Max')}
            </ToggleButton>
            <ToggleButton value="period" aria-label={t('Time Period Max')}>
              {t('Time Period Max')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          mb: { xs: '2rem', sm: '1rem' },
        }}
      >
        {isFetchingData && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(2px)',
              zIndex: 10,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {selectedDeviceData && (
          <DeviceGraph
            containerRef={chartRef}
            max_height={selectedDevice.backend_max_height_mm}
            data={selectedDeviceData}
            show_rainfall={showRainfall}
            show_water_level={showRawWaterLevel || showFilteredWaterLevel}
            show_bench_height={showBenchHeight}
            bench_height_mm={selectedDevice.bench_height_mm}
            show_pipe_diameter={showPipeDiameter}
            pipe_diameter_mm={selectedDevice.pipe_diameter_mm}
            min_time={periodStartDate.unix()}
            max_time={periodEndDate.unix()}
            fit_y_axis_to_data={fitYAxisToData}
            isFiltered={showFilteredWaterLevel}
            warning_threshold={
              (selectedDevice.warning_level_percentage *
                selectedDevice.backend_max_height_mm) /
              100
            }
            peakWaterTime={maxWaterLevel?.timestamp ?? null}
            peakRainTime={maxRainfallLevel?.timestamp ?? null}
            show_maximums={showMaximums}
          />
        )}
        {!selectedDeviceData && isFetchingData && (
          <Box sx={{ height: 300, width: '100%' }} />
        )}
      </Box>

      <DeviceDataRangeStats
        showWaterLevel={showRawWaterLevel || showFilteredWaterLevel}
        showRainfallLevel={showRainfall}
        maxHeight={selectedDevice.backend_max_height_mm}
      />

      <Stack direction="row" gap={1}>
        <DeviceInfoSection.Button
          data-testid="Download Data Button"
          onClick={() => setShowDownload(true)}
        >
          {t('Download Data')}
        </DeviceInfoSection.Button>
        <DeviceScreenshotButton chartRef={chartRef} />
      </Stack>
      <DownloadCSVModal
        open={showDownload}
        setOpen={open => {
          setShowDownload(open);
        }}
        initial_start_date={periodStartDate.toDate()}
        initial_end_date={periodEndDate.toDate()}
        show_rainfall={showRainfall}
        show_water_level={showRawWaterLevel}
        show_filtered_water_level={showFilteredWaterLevel}
      />
    </DeviceInfoSection>
  );
}
